import {
  foundationElectricalLoadPredicate,
  foundationPropPredicate,
  foundationUtilityElectricalLoadPredicate,
  foundationUtilityPropPredicate,
} from "building-generation/utils/foundation";
import type { Building } from "building-types/building/buildingTypes";
import type { LoadView } from "building-types/electrical/electricalViewTypes";
import type { LabelableEntityBase } from "building-types/entities/labelTypes";
import { PropCategory } from "building-types/props/propCategoryTypes";
import type { Prop } from "building-types/props/propTypes";
import type { GeneratedRailing } from "building-types/settings/railingTypes";
import type { ViewportRenderStyle } from "building-types/views/planSets";
import type { SheetGeometryCategoryTable } from "building-types/views/sheetGeometryCategory";
import { SheetGeometryCategory, SheetGeometryPropCategories } from "building-types/views/sheetGeometryCategory";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { FLOOR_PLAN_CUT_PLANE } from "common/constants/buildingObjects";
import type { AbstractPlanGeometrySource, SheetGeometryFilter } from "doc/plans/planGeometrySource";
import { SheetGeometrySourceFilter } from "doc/plans/planGeometrySource";
import { filterForCondition, skipForEntityCondition } from "doc/plans/planGeometrySource";
import type {
  Categorized3DGeometry,
  SheetGeometrySelector,
  TaggedPlanGeometry,
} from "doc/plans/planGeometrySourceTypes";
import { entityContainsBuiltInLabels } from "doc/utils/builtInLabelUtils";
import { entitiesContainMatchingLabel } from "doc/utils/labelTools";
import { propHeightPredicate } from "./entities/planProps";

// Everything in this file should be considered deprecated and will be converted to a user-controllable selector.
// https://linear.app/higharc/issue/SDO-1569/expose-built-in-selectors-and-remove-view-template-reference-type

function makeSelectorsForPropCategories(
  selector: SheetGeometrySelector,
): SheetGeometryCategoryTable<SheetGeometrySelector> {
  const selectors: SheetGeometryCategoryTable<SheetGeometrySelector> = {};

  for (const category of SheetGeometryPropCategories) {
    selectors[category] = selector;
  }

  return selectors;
}

function wainscotingFilter(levelId: string, building: Building): SheetGeometryFilter {
  const {
    levels,
    settings: {
      wainscoting: { showWainscotingOnPlans },
    },
  } = building;

  return filterForCondition(() => !!showWainscotingOnPlans && levels[levelId].floorIndex >= 0);
}

export interface BuiltInFilterSelectorsAndFilters {
  readonly builtInSelectors: SheetGeometryCategoryTable<SheetGeometrySelector>;
  readonly builtInFilters: SheetGeometryCategoryTable<SheetGeometryFilter>;
}

function propPlanPredicate(
  levelId: string,
  building: Building,
  renderStyleMode: ViewportRenderStyle = "default",
): SheetGeometrySelector {
  return (g) => {
    const prop = g.buildingEntity as Prop;

    if (!(prop.room?.level.id === levelId && propHeightPredicate(prop))) {
      return false;
    }

    const propAttributes = building.settings.props[prop.src];

    if (!propAttributes) {
      return false;
    }

    if (renderStyleMode !== "showroomFloorPlan" && propAttributes.category === PropCategory.FURNITURE) {
      return false;
    }

    if (renderStyleMode === "showroomFloorPlan" && prop.z - prop.room.level.floorElevation > FLOOR_PLAN_CUT_PLANE) {
      return false;
    }

    return true;
  };
}

const showroomAnnotationCategories: readonly SheetGeometryCategory[] = [
  SheetGeometryCategory.ANNOT_DETAIL,
  SheetGeometryCategory.ANNOT_LINEWORK,
  SheetGeometryCategory.ANNOT_NOTE,
  SheetGeometryCategory.ANNOT_DIM_LABEL,
  SheetGeometryCategory.ANNOT_DIM_LINEWORK,
  SheetGeometryCategory.ANNOT_DIM_TICK,
  SheetGeometryCategory.ANNOT_DIM_WITNESS,
];

function showroomPlanSelector(g: SheetGeometry | Categorized3DGeometry): boolean {
  const entity = g.buildingEntity as LabelableEntityBase | undefined;
  return !!entity && entityContainsBuiltInLabels(entity, "addToShowroomPlan");
}

function makeShowroomFloorPlanSelectorTable(): SheetGeometryCategoryTable<SheetGeometrySelector> {
  const selectors: SheetGeometryCategoryTable<SheetGeometrySelector> = {
    [SheetGeometryCategory.CEILING]: showroomPlanSelector,
  };

  for (const category of showroomAnnotationCategories) {
    selectors[category] = showroomPlanSelector;
  }

  return selectors;
}

function makeRailingSelectorTable(
  renderStyleMode: ViewportRenderStyle = "default",
): SheetGeometryCategoryTable<SheetGeometrySelector> {
  const railingSelector = (g: SheetGeometry | Categorized3DGeometry): boolean => {
    const railing = g.buildingEntity as GeneratedRailing;
    if (renderStyleMode === "showroomFloorPlan") {
      return railing.includeRailInShowRoomPlan;
    }

    return railing.includeRailInPlanDrawing;
  };
  const railingPostSelector = (g: SheetGeometry | Categorized3DGeometry): boolean => {
    const railing = g.buildingEntity as GeneratedRailing;
    if (renderStyleMode === "showroomFloorPlan") {
      return !!railing.includePostsInShowRoomPlan;
    }

    return !!railing.includePostsInPlanDrawing;
  };

  return {
    [SheetGeometryCategory.RAILING_PROJECTED]: railingSelector,
    [SheetGeometryCategory.RAILING_ABOVE]: railingSelector,
    [SheetGeometryCategory.RAILING_POST]: railingPostSelector,

    [SheetGeometryCategory.RAILING_PROJECTED_STAIR_ONLY]: railingSelector,
    [SheetGeometryCategory.RAILING_ABOVE_STAIR_ONLY]: railingSelector,
    [SheetGeometryCategory.RAILING_POST_STAIR_ONLY]: railingPostSelector,
  };
}

function filterForRailing(
  renderStyleMode: ViewportRenderStyle = "default",
): (source: AbstractPlanGeometrySource, tag: SheetGeometryCategory) => Promise<TaggedPlanGeometry[]> {
  return skipForEntityCondition<GeneratedRailing>((railing) => {
    if (renderStyleMode === "showroomFloorPlan") {
      return !railing.includeRailInShowRoomPlan;
    }

    return !railing.includeRailInPlanDrawing;
  });
}

function filterForRailingPost(
  renderStyleMode: ViewportRenderStyle = "default",
): (source: AbstractPlanGeometrySource, tag: SheetGeometryCategory) => Promise<TaggedPlanGeometry[]> {
  return skipForEntityCondition<GeneratedRailing>((railing) => {
    if (renderStyleMode === "showroomFloorPlan") {
      return !railing.includePostsInShowRoomPlan;
    }

    return !railing.includePostsInPlanDrawing;
  });
}

function makeRailingFilterTable(
  renderStyleMode: ViewportRenderStyle = "default",
): SheetGeometryCategoryTable<SheetGeometryFilter> {
  return {
    [SheetGeometryCategory.RAILING_PROJECTED]: filterForRailing(renderStyleMode),
    [SheetGeometryCategory.RAILING_ABOVE]: filterForRailing(renderStyleMode),
    [SheetGeometryCategory.RAILING_POST]: filterForRailingPost(renderStyleMode),

    [SheetGeometryCategory.RAILING_PROJECTED_STAIR_ONLY]: filterForRailing(renderStyleMode),
    [SheetGeometryCategory.RAILING_ABOVE_STAIR_ONLY]: filterForRailing(renderStyleMode),
    [SheetGeometryCategory.RAILING_POST_STAIR_ONLY]: filterForRailingPost(renderStyleMode),
  };
}

export function makeBuiltInFloorPlanSelectorsAndFilters(
  building: Building,
  levelId: string,
  renderStyleMode: ViewportRenderStyle = "default",
): BuiltInFilterSelectorsAndFilters {
  const { levels, floorPlates } = building;

  const propPredicate = propPlanPredicate(levelId, building, renderStyleMode);

  return {
    builtInSelectors: {
      ...makeSelectorsForPropCategories(propPredicate),
      [SheetGeometryCategory.PROP_TAG]: propPredicate,
      ...makeRailingSelectorTable(renderStyleMode),
      ...(renderStyleMode === "showroomFloorPlan" ? makeShowroomFloorPlanSelectorTable() : {}),
    },
    builtInFilters: {
      ...makeRailingFilterTable(renderStyleMode),
      [SheetGeometryCategory.STEM_WALL_UNIONED_EDGES]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.PIER]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.PIER_HIDDEN]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.PERIMS_WALL]: filterForCondition(
        () => levels[levelId].floorIndex !== 0 && floorPlates[levelId].perimeterPolyLine2s.length > 0,
      ),
      [SheetGeometryCategory.WAINSCOTING_CUT]: wainscotingFilter(levelId, building),
      [SheetGeometryCategory.WAINSCOTING_PROJECTED]: wainscotingFilter(levelId, building),
    },
  };
}

function makeBuiltInFoundationPlanSelectorsAndFiltersBase(
  building: Building,
  levelId: string,
): BuiltInFilterSelectorsAndFilters {
  const { levels } = building;

  const propPredicate = (g: SheetGeometry | Categorized3DGeometry) => {
    return foundationPropPredicate(building, levelId)?.(g.buildingEntity as Prop);
  };

  return {
    builtInSelectors: {
      ...makeSelectorsForPropCategories(propPredicate),
      [SheetGeometryCategory.PROP_TAG]: propPredicate,
    },
    builtInFilters: {
      // different cut plane for basement - lineweights need to be adjusted
      [SheetGeometryCategory.STAIR_WALLS]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.STAIR_ABOVE]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.STAIR_PROJECTED]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.STAIR]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.ELECTRICAL_LOADS]: skipForEntityCondition((e) => {
        return !foundationElectricalLoadPredicate(building, levelId)?.(e as LoadView);
      }),
      // The load's plan label is its own category now, so it needs the same per-load condition as
      // the symbol or a filtered-out load leaves its label behind with nothing to point at.
      [SheetGeometryCategory.ELECTRIC_LOAD_LABEL]: skipForEntityCondition((e) => {
        return !foundationElectricalLoadPredicate(building, levelId)?.(e as LoadView);
      }),
    },
  };
}

export function makeBuiltInFoundationPlanSelectorsAndFilters(
  building: Building,
  levelId: string,
): BuiltInFilterSelectorsAndFilters {
  const { includeFootingInfo } = building.settings.planset;

  const footingFilters = {
    [SheetGeometryCategory.FOOTING]: filterForCondition(() => includeFootingInfo),
    [SheetGeometryCategory.STEM_WALL_BELOW]: filterForCondition(() => includeFootingInfo),
    [SheetGeometryCategory.STEM_WALL_ABOVE]: filterForCondition(() => includeFootingInfo),
    [SheetGeometryCategory.STEM_WALL_ABOVE_GAPS]: filterForCondition(() => includeFootingInfo),
    [SheetGeometryCategory.PIER]: filterForCondition(() => includeFootingInfo),
    [SheetGeometryCategory.PIER_HIDDEN]: filterForCondition(() => includeFootingInfo),
  };

  const selectorsAndFilters = makeBuiltInFoundationPlanSelectorsAndFiltersBase(building, levelId);

  return {
    ...selectorsAndFilters,
    builtInFilters: {
      ...selectorsAndFilters.builtInFilters,
      ...footingFilters,
    },
  };
}

export function makeBuiltInFoundationPlanSelectorsAndFiltersNoSetting(
  building: Building,
  levelId: string,
): BuiltInFilterSelectorsAndFilters {
  return makeBuiltInFoundationPlanSelectorsAndFiltersBase(building, levelId);
}

export function makeBuiltInFoundationUtilityPlanFilter(
  building: Building,
  levelId: string,
): BuiltInFilterSelectorsAndFilters {
  const propPredicate = (g: SheetGeometry | Categorized3DGeometry) => {
    return foundationUtilityPropPredicate(building, levelId)?.(g.buildingEntity as Prop);
  };

  function filterForAddToUtilityPlan(): (
    source: AbstractPlanGeometrySource,
    tag: SheetGeometryCategory,
  ) => Promise<TaggedPlanGeometry[]> {
    return skipForEntityCondition<LabelableEntityBase>((e) => {
      return !entityContainsBuiltInLabels(e, "addToUtilityPlan");
    });
  }

  return {
    builtInSelectors: {
      ...makeSelectorsForPropCategories(propPredicate),
      [SheetGeometryCategory.PROP_TAG]: propPredicate,
    },
    builtInFilters: {
      [SheetGeometryCategory.ELECTRICAL_LOADS]: skipForEntityCondition((e) => {
        return !foundationUtilityElectricalLoadPredicate(building, levelId)?.(e as LoadView);
      }),
      // Same per-load condition as the symbol above - see the floor plan filters.
      [SheetGeometryCategory.ELECTRIC_LOAD_LABEL]: skipForEntityCondition((e) => {
        return !foundationUtilityElectricalLoadPredicate(building, levelId)?.(e as LoadView);
      }),
      [SheetGeometryCategory.CEILING]: filterForAddToUtilityPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_DETAIL]: filterForAddToUtilityPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_LINEWORK]: filterForAddToUtilityPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_NOTE]: filterForAddToUtilityPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LABEL]: filterForAddToUtilityPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LINEWORK]: filterForAddToUtilityPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_WITNESS]: filterForAddToUtilityPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_TICK]: filterForAddToUtilityPlan(),
    },
  };
}

export function makeBuiltInElectricalPlanSelectorsAndFilters(
  building: Building,
  levelId: string,
): BuiltInFilterSelectorsAndFilters {
  const { levels, floorPlates } = building;
  const propCatalog = building.settings.props;

  const propPredicate = propPlanPredicate(levelId, building);

  return {
    builtInSelectors: {
      ...makeSelectorsForPropCategories(propPredicate),
      [SheetGeometryCategory.PROP_TAG]: (g: TaggedPlanGeometry | Categorized3DGeometry) => {
        const prop = g.buildingEntity as Prop;
        const propAttributes = propCatalog[prop.src];
        const drawOnlyProp = propAttributes.hideTagOnElectricalPlan ?? false;
        return propPredicate(g) && !drawOnlyProp;
      },
    },
    builtInFilters: {
      [SheetGeometryCategory.STEM_WALL_UNIONED_EDGES]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.PIER]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.PIER_HIDDEN]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.PERIMS_WALL]: filterForCondition(
        () => levels[levelId].floorIndex !== 0 && floorPlates[levelId].perimeterPolyLine2s.length > 0,
      ),
      ...makeRailingFilterTable(),
    },
  };
}

export function makeBuiltInFloorPlanMaterialsSelectorsAndFilters(
  building: Building,
  levelId: string,
): BuiltInFilterSelectorsAndFilters {
  const { levels, floorPlates } = building;
  const propPredicate = propPlanPredicate(levelId, building);

  return {
    builtInSelectors: makeSelectorsForPropCategories(propPredicate),
    builtInFilters: {
      [SheetGeometryCategory.PERIMS_WALL]: filterForCondition(
        () => levels[levelId].floorIndex !== 0 && floorPlates[levelId].perimeterPolyLine2s.length > 0,
      ),
      ...makeRailingFilterTable(),
    },
  };
}

export function makeBuiltInFloorPlanSummarySelectorsAndFilters(
  building: Building,
  levelId: string,
): BuiltInFilterSelectorsAndFilters {
  const { levels } = building;
  const propPredicate = propPlanPredicate(levelId, building);

  return {
    builtInSelectors: makeSelectorsForPropCategories(propPredicate),
    builtInFilters: {
      [SheetGeometryCategory.PERIMS_FLOORPLATE]: filterForCondition(() => levels[levelId].floorIndex !== 0),
      ...makeRailingFilterTable(),
    },
  };
}

export function makeBuiltInBrochureFloorPlanSummarySelectorsAndFilters(
  building: Building,
  levelId: string,
  displayElectricalOptions: boolean = false,
): BuiltInFilterSelectorsAndFilters {
  const { levels, floorPlates } = building;
  const propPredicate = propPlanPredicate(levelId, building);

  return {
    builtInSelectors: makeSelectorsForPropCategories(propPredicate),
    builtInFilters: {
      [SheetGeometryCategory.PERIMS_WALL]: filterForCondition(
        () => levels[levelId].floorIndex !== 0 && floorPlates[levelId].perimeterPolyLine2s.length > 0,
      ),
      ...makeRailingFilterTable(),
      [SheetGeometryCategory.STEM_WALL_UNIONED_EDGES]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.PIER]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.PIER_HIDDEN]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.ELECTRICAL_CONTROLLERS]: filterForCondition(() => displayElectricalOptions),
      [SheetGeometryCategory.ELECTRICAL_LOADS]: filterForCondition(() => displayElectricalOptions),
      [SheetGeometryCategory.ELECTRIC_LOAD_LABEL]: filterForCondition(() => displayElectricalOptions),
    },
  };
}

export function makeBuiltInStructuralPlanSelectorsAndFilters(
  building: Building,
  levelId: string,
): BuiltInFilterSelectorsAndFilters {
  const { levels } = building;

  const propPredicate = propPlanPredicate(levelId, building);

  return {
    builtInSelectors: {
      ...makeSelectorsForPropCategories(propPredicate),
      [SheetGeometryCategory.PROP_TAG]: propPredicate,
    },
    builtInFilters: {
      [SheetGeometryCategory.STEM_WALL_UNIONED_EDGES]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.PIER]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.PIER_HIDDEN]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.PERIMS_WALL]: filterForCondition(() => levels[levelId].floorIndex !== 0),
      ...makeRailingFilterTable(),
    },
  };
}

export function makeBuiltInRoofSourceFilter(
  useBackgroundAnnotationOnly: boolean,
  labels: {},
): SheetGeometrySourceFilter {
  function filterForAddToRoofPlan(): (
    source: AbstractPlanGeometrySource,
    tag: SheetGeometryCategory,
  ) => Promise<TaggedPlanGeometry[]> {
    return skipForEntityCondition<LabelableEntityBase>((e) => {
      if (useBackgroundAnnotationOnly) {
        return !entitiesContainMatchingLabel(e, { labels });
      } else {
        return !entityContainsBuiltInLabels(e, "addToRoofPlan");
      }
    });
  }

  const filtering = new SheetGeometrySourceFilter(
    {
      [SheetGeometryCategory.CEILING]: filterForAddToRoofPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_DETAIL]: filterForAddToRoofPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_LINEWORK]: filterForAddToRoofPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_NOTE]: filterForAddToRoofPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LABEL]: filterForAddToRoofPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LINEWORK]: filterForAddToRoofPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_WITNESS]: filterForAddToRoofPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_TICK]: filterForAddToRoofPlan(),
    },
    {},
  );

  return filtering;
}

export function makeBuiltInBackgroundPlanSelectorsAndFilters(
  building: Building,
  levelId: string,
  labels: {},
  fullBackgroundVisibility: boolean = false,
): BuiltInFilterSelectorsAndFilters {
  function filterForAddToBackgroundPlan(): (
    source: AbstractPlanGeometrySource,
    tag: SheetGeometryCategory,
  ) => Promise<TaggedPlanGeometry[]> {
    return skipForEntityCondition<LabelableEntityBase>((e) => {
      return !entitiesContainMatchingLabel(e, { labels });
    });
  }

  const { levels } = building;
  const propPredicate = propPlanPredicate(levelId, building);

  return {
    builtInSelectors: {
      ...makeSelectorsForPropCategories(propPredicate),
      [SheetGeometryCategory.PROP_TAG]: propPredicate,
      [SheetGeometryCategory.CEILING]: (g) => entityContainsBuiltInLabels(g.buildingEntity, "addToElectricalPlan"),
    },
    builtInFilters: {
      [SheetGeometryCategory.STEM_WALL_UNIONED_EDGES]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.PIER]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.PIER_HIDDEN]: filterForCondition(() => levels[levelId].floorIndex < 0),
      [SheetGeometryCategory.PERIMS_WALL]: filterForCondition(() => levels[levelId].floorIndex !== 0),
      [SheetGeometryCategory.ALL_LVL_ANNOT_DETAIL]: filterForAddToBackgroundPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_LINEWORK]: filterForAddToBackgroundPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_NOTE]: filterForAddToBackgroundPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LABEL]: filterForAddToBackgroundPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LINEWORK]: filterForAddToBackgroundPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_WITNESS]: filterForAddToBackgroundPlan(),
      [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_TICK]: filterForAddToBackgroundPlan(),
      [SheetGeometryCategory.CASEWORK]: filterForCondition(() => fullBackgroundVisibility),
      ...makeRailingFilterTable(),
    },
  };
}
